/**
 * SchoolAccess — Student ID Cards with QR, Gate Passes, Access Logs, Visitor Management
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { CreditCard, Shield, LogIn, LogOut, Plus, Loader2, User, Ban, Fingerprint, QrCode, UserCheck, Car, Clock, LayoutTemplate, Printer, Trash2, Edit2 } from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api/school${path}`, { ...opts, headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json', ...opts.headers } });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Request failed'); }
  return res.json();
};

const StatCard = ({ label, value, icon: Icon, color }) => (
  <Card><CardContent className="p-3 text-center"><Icon className={`h-5 w-5 mx-auto mb-1 text-${color}-500`} /><p className="text-xl font-bold text-slate-900">{value}</p><p className="text-xs text-slate-500">{label}</p></CardContent></Card>
);

import { QRCodeSVG } from 'qrcode.react';
import { MediaUpload } from '@/components/MediaUpload';

// Real, scannable QR code wrapper
const QRCodeDisplay = ({ payload, size = 80 }) => (
  <div className="border border-slate-200 rounded bg-white p-1" data-testid="school-access-qr">
    <QRCodeSVG value={String(payload || '')} size={size} level="M" includeMargin={false} />
  </div>
);

export const SchoolAccess = ({ tenantId }) => {
  const qc = useQueryClient();
  const [classFilter, setClassFilter] = useState('');
  const [showPassForm, setShowPassForm] = useState(false);
  const [showLogForm, setShowLogForm] = useState(false);
  const [logDate, setLogDate] = useState(new Date().toISOString().split('T')[0]);
  const [passFilter, setPassFilter] = useState('');
  const [passForm, setPassForm] = useState({ student_id: '', pass_type: 'exit', reason: '', authorized_by: '', valid_from: '', valid_until: '' });
  const [logForm, setLogForm] = useState({ student_id: '', log_type: 'entry', gate: 'Main Gate', method: 'manual', notes: '' });
  const [showVisitorForm, setShowVisitorForm] = useState(false);
  const [visitorForm, setVisitorForm] = useState({ name: '', phone: '', email: '', id_number: '', purpose: '', visiting_student_id: '', visiting_staff_name: '', vehicle_number: '', expected_duration: '', notes: '' });
  const [visitorFilter, setVisitorFilter] = useState('checked_in');
  const [selectedQR, setSelectedQR] = useState(null);

  const { data: dashboard } = useQuery({ queryKey: ['access-dashboard', tenantId], queryFn: () => api(`/tenants/${tenantId}/access/dashboard`) });
  const { data: idCards = [], isLoading: loadingCards } = useQuery({ queryKey: ['id-cards', tenantId, classFilter], queryFn: () => api(`/tenants/${tenantId}/access/id-cards${classFilter ? `?class_id=${classFilter}` : ''}`) });
  const { data: passes = [] } = useQuery({ queryKey: ['gate-passes', tenantId, passFilter], queryFn: () => api(`/tenants/${tenantId}/access/gate-passes${passFilter ? `?status=${passFilter}` : ''}`) });
  const { data: logs = [] } = useQuery({ queryKey: ['access-logs', tenantId, logDate], queryFn: () => api(`/tenants/${tenantId}/access/logs?date=${logDate}`) });
  const { data: students = [] } = useQuery({ queryKey: ['access-students', tenantId], queryFn: () => api(`/tenants/${tenantId}/students`).then(d => d.students || d).catch(() => []) });
  const { data: classes = [] } = useQuery({ queryKey: ['school-classes', tenantId], queryFn: () => api(`/tenants/${tenantId}/classes`) });
  const { data: visitors = [] } = useQuery({ queryKey: ['visitors', tenantId, visitorFilter], queryFn: () => api(`/tenants/${tenantId}/access/visitors${visitorFilter ? `?status=${visitorFilter}` : ''}`) });

  const { data: qrData } = useQuery({
    queryKey: ['qr-data', tenantId, selectedQR],
    queryFn: () => api(`/tenants/${tenantId}/access/id-cards/${selectedQR}/qr-data`),
    enabled: !!selectedQR,
  });

  const createPass = useMutation({
    mutationFn: d => api(`/tenants/${tenantId}/access/gate-passes`, { method: 'POST', body: JSON.stringify(d) }),
    onSuccess: r => { toast.success(`Gate pass created: ${r.pass_code}`); qc.invalidateQueries({ queryKey: ['gate-passes'] }); qc.invalidateQueries({ queryKey: ['access-dashboard'] }); setShowPassForm(false); setPassForm({ student_id: '', pass_type: 'exit', reason: '', authorized_by: '', valid_from: '', valid_until: '' }); },
    onError: e => toast.error(e.message),
  });

  const revokePass = useMutation({
    mutationFn: id => api(`/tenants/${tenantId}/access/gate-passes/${id}/revoke`, { method: 'PUT' }),
    onSuccess: () => { toast.success('Pass revoked'); qc.invalidateQueries({ queryKey: ['gate-passes'] }); qc.invalidateQueries({ queryKey: ['access-dashboard'] }); },
    onError: e => toast.error(e.message),
  });

  const createLog = useMutation({
    mutationFn: d => api(`/tenants/${tenantId}/access/logs`, { method: 'POST', body: JSON.stringify(d) }),
    onSuccess: () => { toast.success('Log recorded'); qc.invalidateQueries({ queryKey: ['access-logs'] }); qc.invalidateQueries({ queryKey: ['access-dashboard'] }); setShowLogForm(false); setLogForm({ student_id: '', log_type: 'entry', gate: 'Main Gate', method: 'manual', notes: '' }); },
    onError: e => toast.error(e.message),
  });

  const registerVisitor = useMutation({
    mutationFn: d => api(`/tenants/${tenantId}/access/visitors`, { method: 'POST', body: JSON.stringify(d) }),
    onSuccess: r => { toast.success(`Visitor registered: ${r.visitor_code}`); qc.invalidateQueries({ queryKey: ['visitors'] }); qc.invalidateQueries({ queryKey: ['access-dashboard'] }); setShowVisitorForm(false); setVisitorForm({ name: '', phone: '', email: '', id_number: '', purpose: '', visiting_student_id: '', visiting_staff_name: '', vehicle_number: '', expected_duration: '', notes: '' }); },
    onError: e => toast.error(e.message),
  });

  const checkoutVisitor = useMutation({
    mutationFn: id => api(`/tenants/${tenantId}/access/visitors/${id}/checkout`, { method: 'PUT' }),
    onSuccess: () => { toast.success('Visitor checked out'); qc.invalidateQueries({ queryKey: ['visitors'] }); qc.invalidateQueries({ queryKey: ['access-dashboard'] }); },
    onError: e => toast.error(e.message),
  });

  return (
    <div className="space-y-5" data-testid="school-access">
      <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2"><Fingerprint className="h-5 w-5" /> ID & Access</h2>

      {/* Dashboard */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="access-dashboard">
          <StatCard label="Entries Today" value={dashboard.entries_today} icon={LogIn} color="green" />
          <StatCard label="Exits Today" value={dashboard.exits_today} icon={LogOut} color="red" />
          <StatCard label="On Campus" value={dashboard.on_campus} icon={User} color="indigo" />
          <StatCard label="Active Passes" value={dashboard.active_passes} icon={Shield} color="amber" />
          <StatCard label="Total Students" value={dashboard.total_students} icon={User} color="slate" />
          <StatCard label="Visitors Today" value={dashboard.visitors_today || 0} icon={UserCheck} color="blue" />
          <StatCard label="Active Visitors" value={dashboard.active_visitors || 0} icon={UserCheck} color="purple" />
        </div>
      )}

      <Tabs defaultValue="id-cards">
        <TabsList className="overflow-x-auto flex-nowrap">
          <TabsTrigger value="id-cards" data-testid="access-tab-id-cards"><CreditCard className="h-4 w-4 mr-1" /> ID Cards</TabsTrigger>
          <TabsTrigger value="templates" data-testid="access-tab-templates"><LayoutTemplate className="h-4 w-4 mr-1" /> Templates &amp; Print</TabsTrigger>
          <TabsTrigger value="passes" data-testid="access-tab-passes"><Shield className="h-4 w-4 mr-1" /> Gate Passes {passes.filter(p => p.status === 'active').length > 0 && <Badge className="ml-1 bg-amber-500 text-white text-[10px] h-4 min-w-4 px-1">{passes.filter(p => p.status === 'active').length}</Badge>}</TabsTrigger>
          <TabsTrigger value="visitors" data-testid="access-tab-visitors"><UserCheck className="h-4 w-4 mr-1" /> Visitors {(dashboard?.active_visitors || 0) > 0 && <Badge className="ml-1 bg-purple-500 text-white text-[10px] h-4 min-w-4 px-1">{dashboard.active_visitors}</Badge>}</TabsTrigger>
          <TabsTrigger value="logs" data-testid="access-tab-logs"><LogIn className="h-4 w-4 mr-1" /> Access Logs</TabsTrigger>
        </TabsList>

        {/* ID CARDS TAB */}
        <TabsContent value="id-cards" className="mt-4 space-y-3">
          <div className="flex items-center gap-3">
            <Select value={classFilter} onValueChange={v => setClassFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-full max-w-[200px]" data-testid="id-card-class-filter"><SelectValue placeholder="All Classes" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Classes</SelectItem>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <span className="text-sm text-slate-500">{idCards.length} students</span>
          </div>
          {loadingCards ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : idCards.length === 0 ? (
            <Card className="border-dashed"><CardContent className="py-10 text-center text-slate-400"><CreditCard className="h-8 w-8 mx-auto mb-2 opacity-50" />No students found</CardContent></Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {idCards.map(card => (
                <Card key={card.student_id} className="overflow-hidden" data-testid={`id-card-${card.student_id}`}>
                  <div className="h-1.5" style={{ backgroundColor: card.primary_color }} />
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                        <User className="h-6 w-6 text-slate-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-bold text-sm text-slate-900">{card.name}</p>
                        <p className="text-xs text-slate-500">Adm: {card.admission_number}</p>
                        <p className="text-xs text-slate-500">{card.class_name || 'No class'}</p>
                        <div className="flex gap-2 mt-1">
                          {card.date_of_birth && <span className="text-[10px] text-slate-400">DOB: {card.date_of_birth}</span>}
                          {card.blood_group && <Badge variant="outline" className="text-[10px]">{card.blood_group}</Badge>}
                          {card.gender && <Badge variant="outline" className="text-[10px] capitalize">{card.gender}</Badge>}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 pt-2 border-t flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold" style={{ color: card.primary_color }}>{card.school_name}</p>
                        {card.guardian && <p className="text-[10px] text-slate-500">Guardian: {card.guardian.name} ({card.guardian.phone})</p>}
                      </div>
                      <Button variant="ghost" size="sm" className="h-7" onClick={() => setSelectedQR(card.student_id)} data-testid={`qr-btn-${card.student_id}`}>
                        <QrCode className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* TEMPLATES & BATCH PRINT TAB */}
        <TabsContent value="templates" className="mt-4 space-y-3">
          <IDCardTemplatesTab tenantId={tenantId} classes={classes} idCards={idCards} />
        </TabsContent>

        {/* GATE PASSES TAB */}
        <TabsContent value="passes" className="mt-4 space-y-3">
          <div className="flex items-center justify-between">
            <Select value={passFilter} onValueChange={v => setPassFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-full max-w-[140px] h-8 text-xs" data-testid="pass-status-filter"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="revoked">Revoked</SelectItem></SelectContent>
            </Select>
            <Button size="sm" onClick={() => setShowPassForm(true)} data-testid="create-pass-btn"><Plus className="h-4 w-4 mr-1" /> Issue Pass</Button>
          </div>
          <Card>
            {passes.length === 0 ? <CardContent className="py-10 text-center text-slate-400"><Shield className="h-8 w-8 mx-auto mb-2 opacity-50" />No gate passes</CardContent> : (
              <Table>
                <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Student</TableHead><TableHead>Adm No.</TableHead><TableHead>Type</TableHead><TableHead>Reason</TableHead><TableHead>Status</TableHead><TableHead>Valid Until</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {passes.map(p => (
                    <TableRow key={p.id} data-testid={`pass-row-${p.id}`}>
                      <TableCell className="font-mono text-xs font-medium">{p.pass_code}</TableCell>
                      <TableCell className="font-medium text-sm">{p.student_name}</TableCell>
                      <TableCell className="text-xs text-slate-500">{p.admission_number}</TableCell>
                      <TableCell><Badge variant="outline" className="capitalize text-xs">{p.pass_type}</Badge></TableCell>
                      <TableCell className="text-sm max-w-[150px] truncate">{p.reason || '-'}</TableCell>
                      <TableCell>
                        <Badge className={p.status === 'active' ? 'bg-green-100 text-green-700' : p.status === 'revoked' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}>{p.status}</Badge>
                      </TableCell>
                      <TableCell className="text-xs">{p.valid_until || 'No expiry'}</TableCell>
                      <TableCell>
                        {p.status === 'active' && (
                          <Button variant="ghost" size="sm" className="h-7 text-xs text-red-500" onClick={() => { if (window.confirm('Revoke this pass?')) revokePass.mutate(p.id); }} data-testid={`revoke-${p.id}`}>
                            <Ban className="h-3.5 w-3.5 mr-1" /> Revoke
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        {/* ACCESS LOGS TAB */}
        <TabsContent value="logs" className="mt-4 space-y-3">
          <div className="flex items-center justify-between">
            <Input type="date" value={logDate} onChange={e => setLogDate(e.target.value)} className="w-full max-w-[160px]" data-testid="log-date-picker" />
            <Button size="sm" onClick={() => setShowLogForm(true)} data-testid="add-log-btn"><Plus className="h-4 w-4 mr-1" /> Log Entry</Button>
          </div>
          <Card>
            {logs.length === 0 ? <CardContent className="py-10 text-center text-slate-400"><LogIn className="h-8 w-8 mx-auto mb-2 opacity-50" />No logs for {logDate}</CardContent> : (
              <Table>
                <TableHeader><TableRow><TableHead>Time</TableHead><TableHead>Person</TableHead><TableHead>Type</TableHead><TableHead>Gate</TableHead><TableHead>Method</TableHead><TableHead>Notes</TableHead></TableRow></TableHeader>
                <TableBody>
                  {logs.map(l => (
                    <TableRow key={l.id} data-testid={`log-row-${l.id}`}>
                      <TableCell className="text-xs font-mono">{l.timestamp ? new Date(l.timestamp).toLocaleTimeString() : '-'}</TableCell>
                      <TableCell className="font-medium text-sm">{l.person_name}</TableCell>
                      <TableCell>
                        <Badge className={l.log_type === 'entry' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                          {l.log_type === 'entry' ? <><LogIn className="h-3 w-3 mr-0.5" />IN</> : <><LogOut className="h-3 w-3 mr-0.5" />OUT</>}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{l.gate || '-'}</TableCell>
                      <TableCell><Badge variant="outline" className="capitalize text-[10px]">{l.method}</Badge></TableCell>
                      <TableCell className="text-xs text-slate-500">{l.notes || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        {/* VISITORS TAB */}
        <TabsContent value="visitors" className="mt-4 space-y-3">
          <div className="flex items-center justify-between">
            <Select value={visitorFilter} onValueChange={v => setVisitorFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-full max-w-[150px] h-8 text-xs" data-testid="visitor-status-filter"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Visitors</SelectItem><SelectItem value="checked_in">Checked In</SelectItem><SelectItem value="checked_out">Checked Out</SelectItem></SelectContent>
            </Select>
            <Button size="sm" onClick={() => setShowVisitorForm(true)} data-testid="register-visitor-btn"><Plus className="h-4 w-4 mr-1" /> Register Visitor</Button>
          </div>
          <Card>
            {visitors.length === 0 ? (
              <CardContent className="py-10 text-center text-slate-400"><UserCheck className="h-8 w-8 mx-auto mb-2 opacity-50" />No visitors</CardContent>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Purpose</TableHead>
                    <TableHead>Visiting</TableHead>
                    <TableHead>Vehicle</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Checked In</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visitors.map(v => (
                    <TableRow key={v.id} data-testid={`visitor-row-${v.id}`}>
                      <TableCell className="font-mono text-xs font-medium">{v.visitor_code}</TableCell>
                      <TableCell className="font-medium text-sm">{v.name}</TableCell>
                      <TableCell className="text-xs">{v.phone || '-'}</TableCell>
                      <TableCell className="text-sm max-w-[120px] truncate">{v.purpose || '-'}</TableCell>
                      <TableCell className="text-sm">{v.visiting_student_name || v.visiting_staff_name || '-'}</TableCell>
                      <TableCell className="text-xs">{v.vehicle_number ? <span className="flex items-center gap-1"><Car className="h-3 w-3" />{v.vehicle_number}</span> : '-'}</TableCell>
                      <TableCell>
                        <Badge className={v.status === 'checked_in' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>{v.status === 'checked_in' ? 'In Campus' : 'Left'}</Badge>
                      </TableCell>
                      <TableCell className="text-xs">{v.checked_in_at ? new Date(v.checked_in_at).toLocaleTimeString() : '-'}</TableCell>
                      <TableCell>
                        {v.status === 'checked_in' && (
                          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => checkoutVisitor.mutate(v.id)} data-testid={`checkout-visitor-${v.id}`}>
                            <LogOut className="h-3 w-3 mr-1" /> Check Out
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Gate Pass Form */}
      <Dialog open={showPassForm} onOpenChange={setShowPassForm}>
        <DialogContent>
          <DialogHeader><DialogTitle>Issue Gate Pass</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Student *</Label>
              <Select value={passForm.student_id} onValueChange={v => setPassForm({ ...passForm, student_id: v })}>
                <SelectTrigger data-testid="pass-student-select"><SelectValue placeholder="Select student" /></SelectTrigger>
                <SelectContent>{(Array.isArray(students) ? students : []).map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} ({s.admission_number})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Pass Type</Label>
                <Select value={passForm.pass_type} onValueChange={v => setPassForm({ ...passForm, pass_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="exit">Exit</SelectItem><SelectItem value="entry">Entry</SelectItem><SelectItem value="visitor">Visitor</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Valid Until</Label><Input value={passForm.valid_until} onChange={e => setPassForm({ ...passForm, valid_until: e.target.value })} placeholder="2026-04-08 17:00" /></div>
            </div>
            <div><Label>Reason</Label><Textarea value={passForm.reason} onChange={e => setPassForm({ ...passForm, reason: e.target.value })} rows={2} /></div>
            <div><Label>Authorized By</Label><Input value={passForm.authorized_by} onChange={e => setPassForm({ ...passForm, authorized_by: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => createPass.mutate(passForm)} disabled={!passForm.student_id || createPass.isPending} data-testid="save-pass-btn">
              {createPass.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Issue Pass
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Access Log Form */}
      <Dialog open={showLogForm} onOpenChange={setShowLogForm}>
        <DialogContent aria-describedby="log-form-desc">
          <DialogHeader><DialogTitle>Record Access Log</DialogTitle><p id="log-form-desc" className="sr-only">Log an entry or exit record</p></DialogHeader>
          <div className="space-y-3">
            <div><Label>Student</Label>
              <Select value={logForm.student_id} onValueChange={v => setLogForm({ ...logForm, student_id: v })}>
                <SelectTrigger data-testid="log-student-select"><SelectValue placeholder="Select student" /></SelectTrigger>
                <SelectContent>{(Array.isArray(students) ? students : []).map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div><Label>Type</Label>
                <Select value={logForm.log_type} onValueChange={v => setLogForm({ ...logForm, log_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="entry">Entry</SelectItem><SelectItem value="exit">Exit</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Gate</Label><Input value={logForm.gate} onChange={e => setLogForm({ ...logForm, gate: e.target.value })} /></div>
              <div><Label>Method</Label>
                <Select value={logForm.method} onValueChange={v => setLogForm({ ...logForm, method: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="manual">Manual</SelectItem><SelectItem value="id_card">ID Card</SelectItem><SelectItem value="qr">QR Code</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Notes</Label><Input value={logForm.notes} onChange={e => setLogForm({ ...logForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => createLog.mutate(logForm)} disabled={!logForm.student_id || createLog.isPending} data-testid="save-log-btn">
              {createLog.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Register Visitor Form */}
      <Dialog open={showVisitorForm} onOpenChange={setShowVisitorForm}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" aria-describedby="visitor-form-desc">
          <DialogHeader><DialogTitle>Register Visitor</DialogTitle><p id="visitor-form-desc" className="sr-only">Register a new visitor to the school</p></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name *</Label><Input value={visitorForm.name} onChange={e => setVisitorForm({ ...visitorForm, name: e.target.value })} data-testid="visitor-name" /></div>
              <div><Label>Phone</Label><Input value={visitorForm.phone} onChange={e => setVisitorForm({ ...visitorForm, phone: e.target.value })} data-testid="visitor-phone" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email</Label><Input value={visitorForm.email} onChange={e => setVisitorForm({ ...visitorForm, email: e.target.value })} /></div>
              <div><Label>ID Number</Label><Input value={visitorForm.id_number} onChange={e => setVisitorForm({ ...visitorForm, id_number: e.target.value })} placeholder="National ID / Passport" /></div>
            </div>
            <div><Label>Purpose *</Label><Input value={visitorForm.purpose} onChange={e => setVisitorForm({ ...visitorForm, purpose: e.target.value })} placeholder="Parent meeting, Delivery, etc." data-testid="visitor-purpose" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Visiting Student</Label>
                <Select value={visitorForm.visiting_student_id || 'none'} onValueChange={v => setVisitorForm({ ...visitorForm, visiting_student_id: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="visitor-student-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {(Array.isArray(students) ? students : []).map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Visiting Staff</Label><Input value={visitorForm.visiting_staff_name} onChange={e => setVisitorForm({ ...visitorForm, visiting_staff_name: e.target.value })} placeholder="Staff name" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Vehicle Number</Label><Input value={visitorForm.vehicle_number} onChange={e => setVisitorForm({ ...visitorForm, vehicle_number: e.target.value })} placeholder="ABC 1234" /></div>
              <div><Label>Expected Duration</Label><Input value={visitorForm.expected_duration} onChange={e => setVisitorForm({ ...visitorForm, expected_duration: e.target.value })} placeholder="30 min, 1 hour" /></div>
            </div>
            <div><Label>Notes</Label><Textarea value={visitorForm.notes} onChange={e => setVisitorForm({ ...visitorForm, notes: e.target.value })} rows={2} /></div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => registerVisitor.mutate(visitorForm)} disabled={!visitorForm.name || registerVisitor.isPending} data-testid="save-visitor-btn">
              {registerVisitor.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Register & Check In
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={!!selectedQR} onOpenChange={() => setSelectedQR(null)}>
        <DialogContent className="max-w-sm" aria-describedby="qr-desc">
          <DialogHeader><DialogTitle>Student QR Code</DialogTitle><p id="qr-desc" className="sr-only">QR code for student ID card</p></DialogHeader>
          {qrData ? (
            <div className="flex flex-col items-center space-y-4 py-4">
              <QRCodeDisplay payload={qrData.qr_payload} size={160} />
              <div className="text-center">
                <p className="font-semibold">{qrData.name}</p>
                <p className="text-sm text-slate-500">Adm# {qrData.admission_number}</p>
                {qrData.class_name && <p className="text-xs text-slate-400">{qrData.class_name}</p>}
                <p className="text-xs text-indigo-600 font-medium mt-1">{qrData.school_name}</p>
              </div>
              <div className="bg-slate-50 rounded p-2 text-[10px] text-slate-400 break-all max-w-full">
                {qrData.qr_payload}
              </div>
            </div>
          ) : (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SchoolAccess;

// ---------- Phase 2: ID Card Templates + Batch Print + Issued Cards ----------
function IDCardTemplatesTab({ tenantId, classes, idCards }) {
  const qc = useQueryClient();
  const { data: templates = [] } = useQuery({ queryKey: ['id-card-templates', tenantId], queryFn: () => api(`/tenants/${tenantId}/access/card-templates`) });
  const { data: issued = [] } = useQuery({ queryKey: ['id-cards-issued', tenantId], queryFn: () => api(`/tenants/${tenantId}/access/issued-cards`) });
  const [showTpl, setShowTpl] = useState(false);
  const [showBatch, setShowBatch] = useState(false);
  const [showExpiry, setShowExpiry] = useState(false);
  const [editing, setEditing] = useState(null);
  const emptyTpl = { name: '', target_role: 'student', bg_color: '#1e3a5f', accent_color: '#f59e0b', text_color: '#ffffff', logo_url: '', fields_to_show: ['name', 'admission_number', 'class_name', 'blood_group', 'guardian'], include_qr: true, include_photo: true, include_parent_qr: false, expiry_months: 12, access_zones: [], orientation: 'portrait', is_default: false, school_name_override: '', back_note: '' };
  const [tplForm, setTplForm] = useState(emptyTpl);

  const saveTpl = useMutation({
    mutationFn: () => editing
      ? api(`/tenants/${tenantId}/access/card-templates/${editing}`, { method: 'PUT', body: JSON.stringify(tplForm) })
      : api(`/tenants/${tenantId}/access/card-templates`, { method: 'POST', body: JSON.stringify(tplForm) }),
    onSuccess: () => { toast.success(editing ? 'Template updated' : 'Template created'); qc.invalidateQueries({ queryKey: ['id-card-templates'] }); setShowTpl(false); setEditing(null); setTplForm(emptyTpl); },
    onError: e => toast.error(e.message),
  });
  const delTpl = useMutation({
    mutationFn: id => api(`/tenants/${tenantId}/access/card-templates/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries({ queryKey: ['id-card-templates'] }); },
  });
  const revokeMut = useMutation({
    mutationFn: id => api(`/tenants/${tenantId}/access/issued-cards/${id}/revoke`, { method: 'POST' }),
    onSuccess: () => { toast.success('Card revoked'); qc.invalidateQueries({ queryKey: ['id-cards-issued'] }); },
  });

  const FIELDS = ['name', 'admission_number', 'class_name', 'blood_group', 'guardian', 'date_of_birth', 'gender'];
  const ZONE_PRESETS = ['main_gate', 'library', 'lab-1', 'lab-2', 'dormitory-male', 'dormitory-female', 'cafeteria', 'chapel', 'staff_room'];

  return (
    <div className="space-y-4" data-testid="id-card-templates-tab">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-base font-semibold">Card Templates</h3>
          <p className="text-xs text-slate-500">Design reusable front-of-card layouts and batch-print CR80 PVC cards.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setShowExpiry(true)} data-testid="open-expiry-panel-btn"><Clock className="h-4 w-4 mr-1" />Expiry &amp; Notifications</Button>
          <Button size="sm" variant="outline" onClick={() => setShowBatch(true)} data-testid="open-batch-print-btn"><Printer className="h-4 w-4 mr-1" />Batch Print</Button>
          <Button size="sm" onClick={() => { setEditing(null); setTplForm(emptyTpl); setShowTpl(true); }} data-testid="new-template-btn"><Plus className="h-4 w-4 mr-1" />New Template</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {templates.length === 0 && <p className="col-span-full text-center text-slate-400 py-10">No templates yet. Create one to enable batch printing.</p>}
        {templates.map(t => (
          <Card key={t.id} data-testid={`template-${t.id}`} className="overflow-hidden">
            <div className="h-14 flex items-center px-3" style={{ background: `linear-gradient(135deg, ${t.bg_color}, ${t.bg_color}dd)`, color: t.text_color }}>
              <div className="flex-1">
                <p className="text-xs font-bold">{t.school_name_override || 'School'}</p>
                <p className="text-[10px] opacity-80 capitalize">{t.target_role} · {t.orientation}</p>
              </div>
              {t.is_default && <Badge className="bg-white/20 text-white text-[9px]">Default</Badge>}
            </div>
            <CardContent className="p-3">
              <p className="font-semibold text-sm">{t.name}</p>
              <p className="text-[11px] text-slate-500">Expires in {t.expiry_months}mo · {(t.fields_to_show || []).length} fields</p>
              {(t.access_zones || []).length > 0 && <div className="flex flex-wrap gap-1 mt-1">{t.access_zones.slice(0, 4).map(z => <Badge key={z} variant="outline" className="text-[9px]">{z}</Badge>)}</div>}
              <div className="flex gap-1 mt-2">
                <Button size="sm" variant="outline" onClick={() => { setEditing(t.id); setTplForm({ ...emptyTpl, ...t }); setShowTpl(true); }} data-testid={`edit-template-${t.id}`}><Edit2 className="h-3 w-3" /></Button>
                <Button size="sm" variant="ghost" className="text-red-500" onClick={() => { if (window.confirm('Delete template?')) delTpl.mutate(t.id); }}><Trash2 className="h-3 w-3" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Issued cards registry */}
      <div>
        <h3 className="text-base font-semibold mt-4">Issued Cards</h3>
        <p className="text-xs text-slate-500 mb-2">Live registry of physical cards printed. Revoke to disable access.</p>
        <Card><CardContent className="p-0">
          <Table>
            <TableHeader><TableRow><TableHead>Serial</TableHead><TableHead>Recipient</TableHead><TableHead>Role</TableHead><TableHead>Zones</TableHead><TableHead>Expires</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {issued.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-6 text-slate-400">No cards issued yet.</TableCell></TableRow>}
              {issued.slice(0, 50).map(i => (
                <TableRow key={i.id} data-testid={`issued-card-${i.id}`}>
                  <TableCell className="text-xs font-mono">{i.serial}</TableCell>
                  <TableCell className="text-sm">{i.recipient_name || i.recipient_id}</TableCell>
                  <TableCell className="text-xs capitalize">{i.target_role}</TableCell>
                  <TableCell><div className="flex flex-wrap gap-1">{(i.access_zones || []).slice(0, 3).map(z => <Badge key={z} variant="outline" className="text-[9px]">{z}</Badge>)}</div></TableCell>
                  <TableCell className="text-xs text-slate-500">{i.expires_at || '—'}</TableCell>
                  <TableCell><Badge className={i.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>{i.status}</Badge></TableCell>
                  <TableCell>{i.status === 'active' && <Button size="sm" variant="ghost" className="text-red-500" onClick={() => revokeMut.mutate(i.id)} data-testid={`revoke-${i.id}`}>Revoke</Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent></Card>
      </div>

      {/* Template dialog */}
      <Dialog open={showTpl} onOpenChange={v => { if (!v) { setShowTpl(false); setEditing(null); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? 'Edit Template' : 'New ID Card Template'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-xs">Template Name</Label><Input value={tplForm.name} onChange={e => setTplForm({ ...tplForm, name: e.target.value })} placeholder="Student 2026 Standard" data-testid="template-name-input" /></div>
            <div><Label className="text-xs">Target Role</Label>
              <Select value={tplForm.target_role} onValueChange={v => setTplForm({ ...tplForm, target_role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{['student', 'staff', 'alumni', 'visitor'].map(r => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Orientation</Label>
              <Select value={tplForm.orientation} onValueChange={v => setTplForm({ ...tplForm, orientation: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="portrait">Portrait</SelectItem><SelectItem value="landscape">Landscape</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Background Color</Label><Input type="color" value={tplForm.bg_color} onChange={e => setTplForm({ ...tplForm, bg_color: e.target.value })} /></div>
            <div><Label className="text-xs">Accent Color</Label><Input type="color" value={tplForm.accent_color} onChange={e => setTplForm({ ...tplForm, accent_color: e.target.value })} /></div>
            <div className="col-span-2"><MediaUpload value={tplForm.logo_url || ''} onChange={(url) => setTplForm({ ...tplForm, logo_url: url })} purpose="logo" label="Logo" testId="card-template-logo-upload" previewClassName="h-16" /></div>
            <div><Label className="text-xs">School Name Override</Label><Input value={tplForm.school_name_override || ''} onChange={e => setTplForm({ ...tplForm, school_name_override: e.target.value })} placeholder="Leave blank to use tenant name" /></div>
            <div><Label className="text-xs">Expiry (months)</Label><Input type="number" value={tplForm.expiry_months} onChange={e => setTplForm({ ...tplForm, expiry_months: Number(e.target.value) })} /></div>
            <div className="col-span-2">
              <Label className="text-xs">Fields to show</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {FIELDS.map(f => (
                  <label key={f} className="flex items-center gap-1 text-xs">
                    <input type="checkbox" checked={(tplForm.fields_to_show || []).includes(f)} onChange={e => setTplForm({ ...tplForm, fields_to_show: e.target.checked ? [...(tplForm.fields_to_show || []), f] : (tplForm.fields_to_show || []).filter(x => x !== f) })} />
                    <span className="capitalize">{f.replace('_', ' ')}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Access Zones (authorised areas)</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {ZONE_PRESETS.map(z => (
                  <label key={z} className="flex items-center gap-1 text-xs">
                    <input type="checkbox" checked={(tplForm.access_zones || []).includes(z)} onChange={e => setTplForm({ ...tplForm, access_zones: e.target.checked ? [...(tplForm.access_zones || []), z] : (tplForm.access_zones || []).filter(x => x !== z) })} />
                    <span>{z.replace(/_/g, ' ')}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="col-span-2 flex items-center gap-4 flex-wrap">
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={tplForm.include_qr !== false} onChange={e => setTplForm({ ...tplForm, include_qr: e.target.checked })} />Include QR code</label>
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={tplForm.include_photo !== false} onChange={e => setTplForm({ ...tplForm, include_photo: e.target.checked })} />Include photo</label>
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!tplForm.include_parent_qr} onChange={e => setTplForm({ ...tplForm, include_parent_qr: e.target.checked })} data-testid="tpl-parent-qr" />Parent companion QR (auto-notifies parent on scan)</label>
              <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!tplForm.is_default} onChange={e => setTplForm({ ...tplForm, is_default: e.target.checked })} />Default for this role</label>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => saveTpl.mutate()} disabled={!tplForm.name || saveTpl.isPending} data-testid="save-template-btn">{saveTpl.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />}{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batch print dialog */}
      {showBatch && <BatchPrintDialog tenantId={tenantId} templates={templates} classes={classes} idCards={idCards} onClose={() => { setShowBatch(false); qc.invalidateQueries({ queryKey: ['id-cards-issued'] }); }} />}

      {/* Expiry reminders + smart notifications */}
      {showExpiry && <ExpiryNotificationsDialog tenantId={tenantId} onClose={() => setShowExpiry(false)} />}
    </div>
  );
}

function BatchPrintDialog({ tenantId, templates, classes, idCards, onClose }) {
  const [form, setForm] = useState({ template_id: '', class_id: '', recipient_ids: [], issue_now: true });
  const [loading, setLoading] = useState(false);
  const run = async () => {
    setLoading(true);
    try {
      const payload = { template_id: form.template_id, issue_now: form.issue_now };
      if (form.class_id) payload.class_id = form.class_id;
      if (form.recipient_ids.length) payload.recipient_ids = form.recipient_ids;
      const res = await api(`/tenants/${tenantId}/access/batch-print`, { method: 'POST', body: JSON.stringify(payload) });
      if (res?.html) {
        const w = window.open('', '_blank');
        w.document.write(res.html);
        w.document.close();
        toast.success(`Printing ${res.card_count} cards${res.issued ? ` · ${res.issued} issued` : ''}`);
      }
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent data-testid="batch-print-dialog">
        <DialogHeader><DialogTitle>Batch Print ID Cards</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Template</Label>
            <Select value={form.template_id} onValueChange={v => setForm({ ...form, template_id: v })}>
              <SelectTrigger data-testid="batch-print-template"><SelectValue placeholder="Choose template…" /></SelectTrigger>
              <SelectContent>{templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Option A — Entire Class</Label>
            <Select value={form.class_id || 'none'} onValueChange={v => setForm({ ...form, class_id: v === 'none' ? '' : v, recipient_ids: [] })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">— pick specific students below —</SelectItem>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {!form.class_id && (
            <div><Label className="text-xs">Option B — Pick Students ({form.recipient_ids.length} selected)</Label>
              <div className="max-h-[200px] overflow-y-auto border rounded p-2 space-y-1">
                {idCards.slice(0, 50).map(s => (
                  <label key={s.student_id} className="flex items-center gap-2 text-xs">
                    <input type="checkbox" checked={form.recipient_ids.includes(s.student_id)} onChange={e => setForm({ ...form, recipient_ids: e.target.checked ? [...form.recipient_ids, s.student_id] : form.recipient_ids.filter(x => x !== s.student_id) })} />
                    <span>{s.name} · {s.admission_number}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.issue_now} onChange={e => setForm({ ...form, issue_now: e.target.checked })} />Register issued cards now (live registry + zone check)</label>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
          <Button onClick={run} disabled={!form.template_id || (!form.class_id && !form.recipient_ids.length) || loading} data-testid="run-batch-print-btn">{loading && <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />}Generate &amp; Print</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ExpiryNotificationsDialog({ tenantId, onClose }) {
  const qc = useQueryClient();
  const [within, setWithin] = useState(30);
  const { data: expiring = [], isLoading: loadingExp, refetch } = useQuery({
    queryKey: ['expiring-cards', tenantId, within],
    queryFn: () => api(`/tenants/${tenantId}/access/expiring-cards?within_days=${within}`),
  });
  const { data: notifs = [], refetch: refetchNotif } = useQuery({
    queryKey: ['smart-notifs', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/access/smart-notifications?limit=30`),
  });
  const sendMut = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/access/send-expiry-reminders?within_days=${within}`, { method: 'POST' }),
    onSuccess: (r) => { toast.success(`${r.sent_email} email${r.sent_email !== 1 ? 's' : ''} · ${r.sent_sms} SMS${r.sent_sms !== 1 ? 's' : ''} sent · ${r.skipped} skipped`); refetchNotif(); },
    onError: e => toast.error(e.message),
  });
  const markReadMut = useMutation({
    mutationFn: id => api(`/tenants/${tenantId}/access/smart-notifications/${id}/mark-read`, { method: 'POST' }),
    onSuccess: () => refetchNotif(),
  });
  const markAllMut = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/access/smart-notifications/mark-all-read`, { method: 'POST' }),
    onSuccess: r => { toast.success(`${r.marked} marked as read`); refetchNotif(); },
  });
  const unread = notifs.filter(n => !n.read).length;
  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl" data-testid="expiry-dialog">
        <DialogHeader>
          <DialogTitle>Expiry Reminders &amp; Smart Notifications</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex justify-between items-center">
                <CardTitle className="text-sm">Cards expiring soon</CardTitle>
                <Badge variant="outline" className="text-[10px]">{expiring.length}</Badge>
              </div>
              <CardDescription className="text-xs">Within the next <Input type="number" value={within} onChange={e => { setWithin(Number(e.target.value) || 30); setTimeout(refetch, 10); }} className="h-6 w-16 inline-block mx-1 text-xs" data-testid="expiry-within-input" /> days.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="max-h-[250px] overflow-y-auto space-y-1">
                {loadingExp && <div className="text-center py-4"><Loader2 className="h-4 w-4 animate-spin inline" /></div>}
                {!loadingExp && expiring.length === 0 && <p className="text-center text-slate-400 text-xs py-4">No cards expiring within this window.</p>}
                {expiring.map(c => (
                  <div key={c.id} className="flex justify-between items-center border rounded p-2 text-xs" data-testid={`expiring-${c.id}`}>
                    <div>
                      <p className="font-medium">{c.recipient_name || c.recipient_id}</p>
                      <p className="text-slate-400 text-[10px]">{c.serial} · expires {c.expires_at}</p>
                    </div>
                    <Badge className={c.days_remaining < 7 ? 'bg-red-100 text-red-700' : c.days_remaining < 30 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}>{c.days_remaining}d</Badge>
                  </div>
                ))}
              </div>
              <Button className="w-full mt-3" size="sm" onClick={() => sendMut.mutate()} disabled={!expiring.length || sendMut.isPending} data-testid="send-expiry-btn">{sendMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> : <Clock className="h-3.5 w-3.5 mr-1" />}Send reminders to all</Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <div className="flex justify-between items-center">
                <CardTitle className="text-sm">Smart Notifications {unread > 0 && <Badge className="bg-indigo-500 text-white text-[10px] ml-1">{unread}</Badge>}</CardTitle>
                <Button size="sm" variant="ghost" onClick={() => markAllMut.mutate()} disabled={!unread} className="h-6 text-xs">Mark all read</Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="max-h-[330px] overflow-y-auto space-y-1">
                {notifs.length === 0 && <p className="text-center text-slate-400 text-xs py-4">No notifications yet.</p>}
                {notifs.map(n => (
                  <div key={n.id} className={`border rounded p-2 text-xs ${n.read ? 'opacity-60' : 'bg-indigo-50/30'}`} data-testid={`notif-${n.id}`}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="font-medium">{n.subject}</p>
                        <p className="text-slate-500 text-[10px]">{n.body}</p>
                        <p className="text-[9px] text-slate-400">{n.created_at ? new Date(n.created_at).toLocaleString() : ''} · {n.type.replace('_', ' ')}</p>
                      </div>
                      {!n.read && <Button size="sm" variant="ghost" onClick={() => markReadMut.mutate(n.id)} className="h-5 text-[10px]">✓</Button>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}

